import { getPlan } from "../../api";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { QueryKey } from "./types";
import {
  convertPlanLongIdToParts,
  parsePlanLongIdIntoUrlPartForApi,
} from "../objectUtilities";
import { isValidApiLongPlanId } from "../planHelpers";
import { useLis } from "../context-hooks/useLis";
import type { Plan, PlanLongId } from "../../@types";
import { useMemo } from "react";
import { useCurrentPlanYear } from "../yearFlagHelpers";
import { useRouteParams } from "../routing-hooks/useRouteParams";
import { useSearchParams } from "../routing-hooks/useSearchParams";
import { useBackendVersions } from "./useBackendVersions";
import { useAppState } from "../context-hooks/useAppState";
import {
  getProviderCoverageEntries,
  getProviderNetworkFailure,
  normalizeCoverageEntries,
} from "../providerNetworkHelpers";
import { useProviders } from "../context-hooks/useProviders";
import { useMapnetCoverageEnabled } from "../flag-hooks/useMapnetCoverage";
import { useZipcodeCoordinates } from "./mapbox/useZipcodeCoordinates";
import { useFacilitiesCoverageEntries } from "../context-hooks/useFacilityCoverageEntries";
import { useFacilitiesForPlan } from "../context-hooks/useFacilitiesForPlan";

export interface UsePlanPayload {
  /** a null value indicates a successful endpoint call that fails the typeguard */
  plan?: Plan | null;
  isLoading: boolean;
  error?: Error;
  prefetchPlan: () => void;
  providerNetworkFailure: boolean;
}

type UsePlanType = (overrides?: UsePlanOverrides) => UsePlanPayload;

type UsePlanOverrides = {
  /** a specific planId, otherwise, we look at the route */
  planId?: PlanLongId;
  /** a specific year to get LIS for (current or next plan year) */
  lisYear?: number;
  /** prevents the query from being called, useful for if you just want the pre-fetch function */
  disabled?: boolean;
  planPreviewHeader?: string;
  /**
   * MAPNET facility coverage entries (CY2027+). Each entry is a bare NPI or
   * "NPI:latitude:longitude". Callers that know the center of search should
   * send it inline. Bare entries fall back to the request-level center.
   */
  facilityCoverageEntries?: string[];
};

/**
 * query hook which fetches a plan
 * @example const plan = usePlan(); // will grab the plan based of the planId in the route
 * @example const plan = usePlan({planId}); // will grab a specific plan
 */
export const usePlan: UsePlanType = (overrides?: UsePlanOverrides) => {
  // * Context
  const { getLisForYear } = useLis();
  const { maxProviderSearchRadius, planType, zipcode } = useAppState();
  const { providers } = useProviders();
  /**
   * MAPNET facility coverage entries (CY2027+). Each entry is a bare NPI or
   * "NPI:latitude:longitude". Callers that know the center of search should
   * send it inline. Bare entries fall back to the request-level center.
   */
  const appStateFacilityCoverageEntries = useFacilitiesCoverageEntries();
  const facilityCoverageEntriesToUse = normalizeCoverageEntries(
    overrides?.facilityCoverageEntries ?? appStateFacilityCoverageEntries
  );

  // * Flags
  const mctCurrentPlanYear = useCurrentPlanYear();

  // * Route
  const { planId: routePlanId } = useRouteParams();
  const { selectedYear } = useSearchParams();

  // * Queries
  const { planDbTimestamp, staleTime } = useBackendVersions();

  // * Constants
  const planLongId = overrides?.planId || routePlanId;
  const apiPlanId = planLongId
    ? parsePlanLongIdIntoUrlPartForApi(planLongId)
    : "";

  const planIdIsValid = isValidApiLongPlanId(apiPlanId);

  /** will use route `year` as a fallback and `mctCurrentPlanYear` if it does not exist */
  const lisToSend = getLisForYear(
    overrides?.lisYear || selectedYear || mctCurrentPlanYear
  );

  // Outside the MAPNET gate for the plan's own year, requests keep the legacy
  // wire format: bare provider NPIs and no facilities param.
  const planYear = planLongId
    ? convertPlanLongIdToParts(planLongId).contract_year
    : undefined;
  const mapnetCoverageEnabled = useMapnetCoverageEnabled(planYear);
  const providerEntries = getProviderCoverageEntries(
    providers,
    mapnetCoverageEnabled
  );
  const facilitiesManagementEnabled = useFacilitiesForPlan({
    contract_year: planYear ?? "",
    plan_type: planType,
  });
  // Sorted, or undefined when empty, for a stable query key.
  const facilityCoverageEntries = facilitiesManagementEnabled
    ? facilityCoverageEntriesToUse
    : undefined;

  // The request-level center is the fallback for coverage entries without
  // inline coordinates. Geocode only when MAPNET applies to the plan's own
  // year and the request will carry entries. The geocode also runs for
  // disabled hooks so prefetchPlan builds the same query key that the
  // destination page's enabled hook will use.
  const sendSearchCenter =
    mapnetCoverageEnabled &&
    (providerEntries.length > 0 || !!facilityCoverageEntries);
  const { coordinates, isResolving: isResolvingCenter } =
    useZipcodeCoordinates(sendSearchCenter);
  // Mapbox coordinates are [longitude, latitude].
  const centerLongitude = coordinates?.[0];
  const centerLatitude = coordinates?.[1];

  // Hold the query while the fallback center geocode is in flight so a
  // MAPNET request cannot fire without its center and then refire.
  const enabled = planIdIsValid && !overrides?.disabled && !isResolvingCenter;

  // * Queries
  const queryClient = useQueryClient();

  const planOptions = queryOptions({
    queryKey: [
      QueryKey.Plan,
      planDbTimestamp,
      apiPlanId,
      lisToSend,
      overrides?.planPreviewHeader,
      providerEntries,
      facilityCoverageEntries,
      zipcode,
      centerLatitude,
      centerLongitude,
      maxProviderSearchRadius,
    ],
    queryFn: () => {
      const lisInfo = lisToSend ? { lis: lisToSend } : {};

      // @TODO - decide whether it's safe to strip out checks on things that are
      // theoretically always defined (`providerEntries`)
      const providerInfo =
        !!providerEntries && providerEntries.length > 0 && !!zipcode
          ? {
              providers: providerEntries,
              zip: zipcode,
              radius: maxProviderSearchRadius,
            }
          : {};

      const centerInfo =
        centerLatitude !== undefined && centerLongitude !== undefined
          ? { latitude: centerLatitude, longitude: centerLongitude }
          : {};

      const facilityInfo = facilityCoverageEntries
        ? { facilities: facilityCoverageEntries }
        : {};

      const queryParams = {
        ...lisInfo,
        ...providerInfo,
        ...centerInfo,
        ...facilityInfo,
      };

      return getPlan({
        id: apiPlanId,
        queryParams,
        ...(overrides?.planPreviewHeader && {
          headers: { "hpms-plan-preview": overrides?.planPreviewHeader },
        }),
      });
    },
  });

  const {
    data,
    error: queryError,
    isLoading,
  } = useQuery({
    ...planOptions,
    enabled,
    staleTime,
  });

  const error = useMemo(() => {
    // error comes from calling the endpoint and not getting a plan back
    if (queryError) return queryError as Error;
    if (!planIdIsValid) {
      // error comes from invalid plan id, where we do not call the endpoint in the first place
      return new Error(`Invalid plan ID (${apiPlanId})`);
    }
    return;
  }, [apiPlanId, planIdIsValid, queryError]);

  const prefetchPlan = () => {
    queryClient.prefetchQuery(planOptions);
  };

  return {
    plan: data?.plan,
    isLoading,
    error,
    prefetchPlan,
    providerNetworkFailure: getProviderNetworkFailure(data?.headers),
  };
};
